import React, { useEffect, useState } from "react";
import { Button, Table, Tag, Spin, message } from "antd";
import { EyeOutlined, FileExcelOutlined } from "@ant-design/icons";
import getPaidMerchantInvoices from "functions/merchant/getPaidMerchantInvocies";
import moneyFormat from "functions/utils/moneyFormat";
import InvoicePreview from "components/modal/merchantInvoicePreview";
import CountPreview from "components/modal/merchantInvoiceCountPreview";
import { VatPreview } from "components/modal/vatPreview";
import * as XLSX from "xlsx";
import dateFormatShort from "functions/utils/dateFormatShort";
import { bg_button, bg_shadow, bg_text } from "constant/css";
import { getMerchantId } from "service/storage";

const MerchantPaidInvoiceList = () => {
  const merchantId = getMerchantId();
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(null);
  const [countPreviewOpen, setCountPreviewOpen] = useState(null);
  const [vatPreviewOpen, setVatPreviewOpen] = useState(null);
  const columns = [
    {
      title: "Нэхэмжлэлийн утга",
      dataIndex: "description",
      key: "description",
      render: (text, r) => (
        <div
          onClick={() => showPreview(r.id)}
          className="flex items-center gap-3"
        >
          <div
            className={`${bg_button} w-8 h-8 bg-green-100 text-gray-100 flex items-center justify-center rounded-full`}
          >
            <EyeOutlined />
          </div>
          <span className="font-medium text-gray-700">{text}</span>
        </div>
      ),
    },
    {
      title: "Нийт дүн",
      dataIndex: "total",
      key: "total",
      render: (value) => (
        <span className="text-gray-700">{moneyFormat(value)} ₮</span>
      ),
    },
    {
      title: "Ашиглах хугацаа",
      dataIndex: "validUntil",
      key: "validUntil",
      render: (value) => <span className="text-gray-500">{value} хүртэл</span>,
    },
    {
      title: "Төлсөн огноо",
      dataIndex: "paidDate",
      key: "paidDate",
      render: (value) => <span className="text-gray-500">{value}</span>,
    },
    {
      title: "Төлөв",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color = "cyan";
        if (status === "Failed") color = "red";
        else if (status === "Pending") color = "orange";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "",
      dataIndex: "vatLog",
      key: "vatLog",
      render: (e, r) => (
        <div className="flex flex-col items-center gap-3">
          {e ? (
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => showVatInfo(e.qrJson)}
              className={`${bg_shadow}  text-[#45a898] text-[11px] px-2.5 py-0.5  shadow-[0_4px_12px_rgba(40,167,69,0.1)] rounded-md border border-transparent shadow-sm hover:shadow-md`}
            >
              <span className={`${bg_text}`}>НӨАТ</span>
            </Button>
          ) : (
            <span> </span>
          )}
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => showCountDetail(r.id)}
            className={`${bg_shadow} bg-white text-[#45a898] text-[11px] px-2.5 py-0.5  shadow-[0_4px_12px_rgba(40,167,69,0.1)] rounded-md border border-transparent shadow-sm hover:shadow-md`}
          >
            <span className={`${bg_text}`}>Задаргаа</span>
          </Button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const paid = await getPaidMerchantInvoices(merchantId);
        setPaidInvoices(paid);
      } catch (error) {
        console.error("API Load Error:", error);
      } finally {
        setLoading(false);
      }
    };
    if (merchantId) fetchData();
  }, [merchantId]);
  const showVatInfo = (qrJson) => {
    setVatPreviewOpen(qrJson);
  };
  const showPreview = (id) => {
    setPreviewOpen(id);
  };
  const showCountDetail = (id) => {
    setCountPreviewOpen(id);
  };

  const downloadList = async () => {
    // download paid invoice list as excel

    if (!paidInvoices || paidInvoices.length === 0) {
      message.error("Мэдээлэл байхгүй байна.");
      return;
    }

    const data = paidInvoices.map((item, index) => ({
      "№": index + 1,
      "Нэхэмжлэлийн утга": item.description,
      "Нийт дүн": item.total,
      "Ашиглах хугацаа": dateFormatShort(item.validUntil),
      "Төлсөн огноо": dateFormatShort(item.paidDate),
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Нэхэмжлэл");

    XLSX.writeFile(workbook, `paid_invoice_list.xlsx`);
  };
  return (
    <Spin spinning={loading} size="large" className={`flex justify-center`}>
      <div
        className={`min-h-screen max-w-6xl mx-auto bg-white p-8 ${bg_shadow} rounded-xl`}
      >
        {previewOpen && (
          <InvoicePreview
            open={previewOpen}
            close={() => setPreviewOpen(null)}
            invoiceId={previewOpen}
          />
        )}
        {vatPreviewOpen && (
          <VatPreview
            open={vatPreviewOpen}
            close={() => setVatPreviewOpen(null)}
            invoiceId={vatPreviewOpen}
          />
        )}
        {countPreviewOpen && (
          <CountPreview
            open={countPreviewOpen}
            close={() => setCountPreviewOpen(null)}
            invoiceId={countPreviewOpen}
          />
        )}
        <div className={` bg-white rounded-xl p-6`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                Төлөлтийн түүх
              </h2>
              <p className="text-sm text-gray-500">Дарж задаргаа харна уу</p>
            </div>
            <Button
              icon={<FileExcelOutlined />}
              type="primary"
              onClick={downloadList}
              className={`${bg_button} text-gray-100 ${bg_shadow}`}
            >
              EXCEL татах
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={paidInvoices}
            pagination={false}
            rowKey="id"
            locale={{ emptyText: "Төлөлтийн түүх олдсонгүй" }}
            className="bg-white rounded-xl shadow-sm"
          />

          {previewOpen && (
            <InvoicePreview
              open={previewOpen}
              close={() => setPreviewOpen(null)}
              invoiceId={previewOpen}
            />
          )}
        </div>
      </div>
    </Spin>
  );
};

export default MerchantPaidInvoiceList;
